import { useContext, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import Tabs from 'app/components/Tabs';
import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import { formatDate } from 'app/utils/date';
import ConfigContext from 'app/common/ConfigContext';
import TeamAvatar from 'app/components/TeamAvatar';

import { getBeginAndEndDates, getPrizepool, getSeriesTeams, getTournamentsLocation } from './utils';
import style from './style.module.scss';

interface Props {
  data: SeriesOverviewBlockMatch;
}

const SeriesOverviewBlockGrad: FC<Props> = ({ data }) => {
  const { lang } = useContext(ConfigContext);
  const { t } = useTranslation();

  const [tab, setTab] = useState<'overview' | 'teams'>('overview');
  const [beginDate, endDate] = getBeginAndEndDates(data.tournaments);

  return (
    <BlockGradTemplate hasData={!!data} title={t('block-series_overview-title')}>
      <Tabs
        selectTab={setTab}
        selectedTab={tab}
        tabs={['overview', 'teams']}
        translationKey="block-series_overview-tabs"
        compact
        className={style.tabs}
      />
      {tab === 'overview' && (
        <div className={style.overviewContainer}>
          <div className={style.overviewColumn}>
            <div className={style.overviewStatName}>{t('block-series_overview-info-location')}</div>
            <div className={style.overviewStatValue}>
              {getTournamentsLocation(data.tournaments)}
            </div>
            <div className={style.overviewStatName}>
              {t('block-series_overview-info-prizepool')}
            </div>
            <div className={style.overviewStatValue}>
              {getPrizepool(data.prizepool_amount, data.prizepool_curency)}
            </div>
          </div>
          <div className={style.overviewColumn}>
            <div className={style.overviewStatName}>
              {t('block-series_overview-info-start_date')}
            </div>
            <div className={style.overviewStatValue}>
              {beginDate
                ? formatDate(beginDate, lang, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })
                : '-'}
            </div>
            <div className={style.overviewStatName}>{t('block-series_overview-info-end_date')}</div>
            <div className={style.overviewStatValue}>
              {endDate
                ? formatDate(endDate, lang, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })
                : '-'}
            </div>
            <div className={style.overviewStatName}>{t('block-series_overview-info-teams')}</div>
            <div className={style.overviewStatValue}>{data.number_of_teams ?? '-'}</div>
          </div>
        </div>
      )}
      {tab === 'teams' && (
        <div>
          {getSeriesTeams(data.tournaments).map(team => (
            <div key={team.id} className={style.team}>
              <div className={style.teamName}>
                <TeamAvatar team={team} />
                {team.name}
              </div>
              <ul className={style.teamPlayersList}>
                {team.players.map(player => (
                  <li key={player.id} className={style.teamPlayer}>
                    <span>{player.role}</span>
                    <span>{player.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </BlockGradTemplate>
  );
};

SeriesOverviewBlockGrad.displayName = 'SeriesOverviewBlockGrad';

export default SeriesOverviewBlockGrad;
